import {useEffect} from 'react'
import {useGetImportStatusQuery} from 'features/Settings/Users/hooks/useGetImportStatusQuery'
import {UserImportStatus} from 'interfaces'
import {useActions} from './useActions'
import {PERMISSIONS, withPermission} from 'rbac'

// This is a module-level singleton flag that controls polling across the app.
// Interacts with the import link (copy/click) and stops once import is complete and no re-auth is required.
// Note: Only one import polling can be active at a time.
//
// This is intentionally non-reactive. The flag is only consumed inside
// the `refetchInterval` callback, which React Query calls on a timer — it
// does not need to trigger a re-render. A module-level variable is simpler
// and avoids adding a context/external store for a value that React never
// renders directly.
let isPollingActive = false

export const startImportStatusPolling = () => {
  isPollingActive = true
}

interface UseUsersImportedOptions {
  pollWhileIncomplete?: boolean
}

export const useUsersImported = (options?: UseUsersImportedOptions) => {
  const {isUserLoggedIn} = useActions()
  // Only users with user management read permissions can check the import status.
  // Today SOC Analysts does not have permission to check the import status.
  const canReadImportStatus = withPermission([PERMISSIONS.USER_MANAGEMENT.READ])

  const {data, isLoading, isSuccess, isError, refetch} =
    useGetImportStatusQuery({
      enabled: isUserLoggedIn && canReadImportStatus,
      refetchInterval: options?.pollWhileIncomplete
        ? data => {
            if (!isPollingActive) return false

            const isDone = data?.importStatus === UserImportStatus.DONE
            const isReauth = Boolean(data?.reauthRequired)
            // Stop polling once import is complete and no re-auth is needed
            if (isDone && !isReauth) {
              isPollingActive = false
              return false
            }
            return 10000
          }
        : undefined,
    })

  // Stop polling when a request fails to avoid hammering a failing endpoint
  useEffect(() => {
    if (isError && options?.pollWhileIncomplete) {
      isPollingActive = false
    }
  }, [isError, options?.pollWhileIncomplete])

  // Stop polling when user logs out
  useEffect(() => {
    if (!isUserLoggedIn) {
      isPollingActive = false
    }
  }, [isUserLoggedIn])

  return {
    usersImported: data?.importStatus === UserImportStatus.DONE,
    isImportStarted: data?.importStatus === UserImportStatus.IN_PROGRESS,
    reauthRequired: data?.reauthRequired ?? false,
    lastUpdated: data?.lastUpdated,
    authProvider: data?.authProvider,
    importStatus: data?.importStatus,
    isLoading,
    isSuccess,
    isError,
    refetch,
  }
}
